import { format } from 'date-fns/format'
import { setDate } from 'date-fns/setDate'
import { setMonth } from 'date-fns/setMonth'
import { setYear } from 'date-fns/setYear'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { createTestId } from 'aem-libs-tests/src/utility/createTestId'
import { Maybe } from 'aem-libs-ts/src/types/utility'

import { CloseButton } from '../../common/close-button'
import { Flex } from '../../common/flex'
import { AriaHiddenLabel } from '../../utility/hidden-aria/HiddenAriaLabel.styled'
import { SelectBox } from '../select-box'
import * as DatepickerStyled from './Datepicker.styled'
import { DateScreen } from './components/DateScreen'
import { daysInWeek, getYearsRange, monthsRange } from './constants'
import { useFocusDateTable } from './hooks/useFocusDateTable'
import cs from './i18n/cs.json'
import en from './i18n/en.json'
import { DisableDatesFn } from './types'


export interface DatepickerProps {
  yearsRange?: number[]
  disableDates?: Maybe<DisableDatesFn>
  mode?: 'default' | 'dateChange'
  testId?: string
  value: Date
  onChange: (value: Date, keepVisible?: boolean) => void
  onYearsChange?: (year: number) => void
  onClose?: () => void
}

export const Datepicker = ({
  value,
  yearsRange,
  mode = 'default',
  disableDates,
  testId,
  onChange,
  onYearsChange,
  onClose
}: DatepickerProps) => {
  const { t, i18n } = useTranslation('dates')
  i18n.addResourceBundle('cs', 'dates', cs)
  i18n.addResourceBundle('en', 'dates', en)

  const [selected, setSelected] = useState<Date>(value)

  const isDateValid = (date: Date) => {
    return !(disableDates && disableDates(date))
  }

  const processChange = (newDate: Date) => {
    setSelected(newDate)
    if (isDateValid(newDate) && mode === 'default') {
      onChange(newDate, true)
    }
  }

  const handleChangeMonth = (value: number) => {
    const newDate = setMonth(selected, value)
    setFocusedDate(newDate)
    processChange(newDate)
  }

  const handleChangeYear = (value: number) => {
    const newDate = setYear(selected, value)
    setFocusedDate(newDate)
    processChange(newDate)
    if (onYearsChange) {
      onYearsChange(value)
    }
  }

  const handleChangeDate = (value: number) => {
    const newDate = setDate(selected, value)
    if (isDateValid(newDate)) {
      onChange(newDate)
    }
  }

  const generateYearsRange = () => {
    return (yearsRange ?? getYearsRange(new Date().getFullYear())).map((y) => ({ label: y.toString(), value: y }))
  }

  const handleChangeMonthYear = (month: number, year: number, day?: number) => {
    const daysInTargetMonth = new Date(year, month + 1, 0).getDate()
    const targetDay = day && day <= daysInTargetMonth ? day : daysInTargetMonth
    const newDate = new Date(year, month, targetDay)
    setFocusedDate(newDate)
    setSelected(newDate)
    if (onYearsChange) {
      onYearsChange(year)
    }
  }

  const usedTestId = testId ?? 'datepicker'

  const datepickerContainerRef = useRef<HTMLTableElement>(null)
  const [focusedDate, setFocusedDate] = useState<Date>(value ? value : new Date())
  const { handleKeyDown } = useFocusDateTable(
    selected,
    handleChangeMonthYear,
    focusedDate,
    setFocusedDate,
    disableDates,
    onChange,
    onClose
  )

  const focusElementRef = useCallback(
    (node: HTMLTableSectionElement) => {
      if (node && focusedDate) {
        const datepickerContainerCurrentHTML = datepickerContainerRef.current?.querySelector(
          '[data-date="' + format(focusedDate, 'yyyy-MM-dd') + '"]'
        ) as HTMLElement
        datepickerContainerCurrentHTML?.focus()
      }
    },
    [focusedDate]
  )

  return (
    <DatepickerStyled.Container data-testid={createTestId(usedTestId, 'wrapper')}>
      <CloseButton
        aria-label={t('MODAL_CLOSE')}
        id={'close-button'}
        type={'button'}
        onClick={() => onClose && onClose()}
        data-testid={createTestId(usedTestId, 'close-button')}
      />
      <DatepickerStyled.DatepickerCard>
        <AriaHiddenLabel aria-live='polite' role='status'>
          {t('MONTH.' + selected.getMonth()) + ' ' + selected.getFullYear()}
        </AriaHiddenLabel>
        <Flex justifyContent={'center'} gap={20} mb={'sm'}>
          <SelectBox
            items={monthsRange.map((m) => ({ label: t('MONTH.' + m.toString()), value: m }))}
            value={selected.getMonth()}
            onChange={handleChangeMonth}
            testId={createTestId(usedTestId, 'months')}
          />
          <SelectBox
            searchEnabled
            simpleSearch
            items={generateYearsRange()}
            value={selected.getFullYear()}
            onChange={handleChangeYear}
            testId={createTestId(usedTestId, 'years')}
          />
        </Flex>
        <div ref={focusElementRef}>
          <DatepickerStyled.DateScreenTable ref={datepickerContainerRef} onKeyDown={handleKeyDown}>
            <thead>
              <tr>
                {daysInWeek.map((d) => (
                  <DatepickerStyled.DayLabel scope='col' key={d}>
                    {t('DAY.' + d.toString())}
                  </DatepickerStyled.DayLabel>
                ))}
              </tr>
            </thead>
            <DateScreen
              customRef={focusElementRef}
              disableDates={disableDates}
              date={selected}
              focusedDate={focusedDate}
              onChangeDate={handleChangeDate}
              testId={createTestId(usedTestId, 'days')}
            />
          </DatepickerStyled.DateScreenTable>
        </div>
      </DatepickerStyled.DatepickerCard>
    </DatepickerStyled.Container>
  )
}
